"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { MultimodalClient } from "./multimodal-client"

export function useMultimodalClient(userId, currentSessionId) {
  const [client, setClient] = useState(null)
  const [isConnected, setIsConnected] = useState(false)
  const [sessionId, setSessionId] = useState(null)
  const [isModelSpeaking, setIsModelSpeaking] = useState(false)
  const [isModelFunctionCall, setIsModelFunctionCall] = useState(false)
  const [onTextReceived, setOnTextReceived] = useState(false)
  const [onUserTextReceived, setOnUserTextReceived] = useState(false)
  const clientRef = useRef(null)

  // Listen for logout event to cleanup immediately
  useEffect(() => {
    const handleLogout = () => {
      if (clientRef.current) {
        try {
          clientRef.current.close()
          clientRef.current = null
        } catch (error) {
          // Silent cleanup on logout
        }
      }
      setClient(null)
      setIsConnected(false)
      setSessionId(null)
    }

    window.addEventListener('zoomey-logout', handleLogout)
    return () => window.removeEventListener('zoomey-logout', handleLogout)
  }, [])

  // Initialize client
  useEffect(() => {
    if (!userId || !currentSessionId) {
      // Silent return - don't log during logout
      // Reset state if user becomes null (e.g., during logout)
      if (client) {
        setClient(null)
        setIsConnected(false)
        setSessionId(null)
      }
      return;
    }

    const initClient = async () => {
      try {
        const newClient = new MultimodalClient(userId, currentSessionId)
        clientRef.current = newClient
        setClient(newClient)

        // Set up event handlers
        newClient.onReady = () => {
          setIsConnected(true)
        }

        newClient.onTextReceived = (text) => {
          setOnTextReceived(text)
        }

        newClient.onUserTextReceived = (text) => {
          setOnUserTextReceived(text)
        }

        newClient.onSessionIdReceived = (id) => {
          setSessionId(id)
        }

        newClient.onAudioReceived = () => {
          setIsModelSpeaking(true)
        }

        newClient.onToolCalling = () => {
          setIsModelFunctionCall(true)
        }

        newClient.onToolCallingEnd = () => {
          setIsModelFunctionCall(false)
        }

        newClient.onTurnComplete = () => {
          setIsModelSpeaking(false)
           setIsModelFunctionCall(false)
        }

        newClient.onInterrupted = () => {
          setIsModelSpeaking(false)
          console.log("now is onInterrupted")
        }

        // Connect to server - only if voice service URL is configured
        const voiceUrl = process.env.REACT_APP_VOICE_WS_URL
        if (voiceUrl) {
          await newClient.connect()
        }
      } catch (error) {
        // Silently handle voice service unavailability - it's optional
        if (error.message !== "Connection timeout") {
          console.warn("Voice service unavailable:", error.message)
        }
        setIsConnected(false)
      }
    }

    initClient()

    // Cleanup on unmount
    // return () => {
    //   if (clientRef.current) {
    //     clientRef.current.close()
    //   }
    // }
    return () => {
      if (clientRef.current) {
        try {
          clientRef.current.close()
          clientRef.current = null
        } catch (error) {
          console.error('Error cleaning up client:', error)
        }
      }
    }
  }, [userId, currentSessionId])

  const connect = async () => {
    if (client) {
      try {
        await client.connect()
        setIsConnected(true)
      } catch (error) {
        console.error("Failed to connect:", error)
        setIsConnected(false)
      }
    }
  }

  const startRecording = async () => {
    if (client) {
      try {
        const success = await client.startRecording()
        return success
      } catch (error) {
        console.error("Failed to start recording:", error)
        return false
      }
    }
    return false
  }

  const stopRecording = () => {
    if (client) {
      client.stopRecording()
    }
  }

  const initializeWebcam = async (videoElement) => {
    if (client && videoElement) {
      try {
        return await client.initializeWebcam(videoElement)
      } catch (error) {
        console.error("Failed to initialize webcam:", error)
        return false
      }
    }
    return false
  }

  const initializeScreenShare = async (videoElement) => {
    if (client && videoElement) {
      try {
        return await client.initializeScreenShare(videoElement)
      } catch (error) {
        console.error("Failed to initialize screen share:", error)
        return false
      }
    }
    return false
  }

  const stopVideo = () => {
    if (client) {
      client.stopVideo()
    }
  }

  return {
    client,
    isConnected,
    sessionId,
    isModelSpeaking,
    onTextReceived,
    onUserTextReceived,
    isModelFunctionCall,
    connect,
    startRecording,
    stopRecording,
    initializeWebcam,
    initializeScreenShare,
    stopVideo,
  }
}
